import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNotificationFromDBContext,
  useUserContext,
  useNotification,
} from 'providers';
import { Link } from 'react-router-dom';
import { getCompanyTypeShortName, shotName } from 'helpers';
import { UserType } from 'models';
import { ModalTenderCreate } from 'modals';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Menu, Bell } from 'lucide-react';
import { ProfilesCard } from 'common';
import Container from 'components/Container';
import NotificationContainer from 'components/NotificationContainer';
import { getNotificationsForUser } from 'api';
import { ProfileAvatar } from 'pages/Profile/components';
import { cn } from 'lib/utils';
import LogoSrc from './logo.png';
import AvatarPlaceholder from './avatar.png';

interface HeaderProps {
  hideNav?: boolean;
  wide?: boolean;
  className?: string;
}

const Header = memo(
  ({ hideNav = false, wide = false, className }: HeaderProps) => {
    const { user, showSignInModal, logout, company, currentProfile } =
      useUserContext();
    const { notifications, setNotifications } = useNotificationFromDBContext();
    const { showNotification } = useNotification();

    const [modalTenderCreateVisible, setModalTenderCreateVisible] =
      useState(false);
    const [notificationsVisible, setNotificationsVisible] = useState(false);
    const [profileDetailsVisible, setProfileDetailsVisible] = useState(false);

    const navigationItems = useMemo(() => [], []);

    const getNotification = useCallback(async () => {
      if (user.authenticated) {
        const response = await getNotificationsForUser({ isRead: false });
        setNotifications(response);
      }
    }, [setNotifications, user.authenticated]);

    useEffect(() => {
      getNotification();
    }, [getNotification]);

    const onClickNotification = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setProfileDetailsVisible(false);
        setNotificationsVisible((prev) => !prev);
      },
      []
    );

    const onClickCloseNotification = useCallback(() => {
      setNotificationsVisible(false);
    }, []);

    const onClickProfile = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setNotificationsVisible(false);
        setProfileDetailsVisible((prev) => !prev);
      },
      []
    );

    const onClickLogin = useCallback(() => {
      showSignInModal(true);
    }, [showSignInModal]);

    const onClickLogout = useCallback(() => {
      logout();
    }, [logout]);

    const onClickCreateTender = useCallback(() => {
      if (user.authenticated) {
        setModalTenderCreateVisible(true);
      } else {
        showSignInModal();
      }
    }, [showSignInModal, user.authenticated]);

    const closeModalTenderCreate = useCallback(() => {
      setModalTenderCreateVisible(false);
    }, []);

    const handleTenderCreateSuccess = useCallback(() => {
      showNotification({ message: 'Заявка создалась', variant: 'success' });
    }, [showNotification]);

    const profileType = useMemo(() => {
      if (currentProfile.currentType === 'user') return 'Личный профиль';
      if (company) return getCompanyTypeShortName(company.type);
      return 'Сотрудник';
    }, [currentProfile, company]);

    const profileName = useMemo(() => {
      return currentProfile.currentType === 'user'
        ? shotName(user)
        : company?.name || 'Компания';
    }, [currentProfile, user, company]);

    return (
      <>
        <header
          className={cn(
            'bg-white shadow-lg h-16 sticky top-0 z-50 md:static md:relative',
            className
          )}
        >
          <Container
            className={cn(
              'flex items-center justify-between h-full px-4',
              wide && 'max-w-full'
            )}
          >
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center">
                <div className="flex items-center w-52 sm:w-60">
                  <img src={LogoSrc} alt="SMR Logo" className="h-8 sm:h-10" />
                  <div className="flex flex-col ml-2">
                    <span className="font-medium text-lg sm:text-xl leading-6 text-foreground">
                      SMR
                    </span>
                    <span className="font-normal text-xs leading-5 tracking-wide text-muted-foreground hidden sm:block">
                      Поиск подряда и поставщиков
                    </span>
                  </div>
                </div>
              </Link>
              {!hideNav && <nav className="hidden lg:block ml-4"></nav>}
              <div className="hidden lg:block ml-4">
                <Button
                  variant="outline"
                  onClick={onClickCreateTender}
                  className="border-border text-foreground hover:bg-muted"
                >
                  Создать объявление
                </Button>
              </div>
            </div>

            <div className="flex items-center">
              <div className="hidden md:flex items-center relative">
                {user.authenticated && (
                  <div className="relative mr-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onClickNotification}
                      className="relative hover:bg-muted transition-colors"
                      aria-label="Уведомления"
                    >
                      <Bell className="h-5 w-5 text-muted-foreground" />
                      {notifications.length > 0 && (
                        <Badge
                          variant="destructive"
                          className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                        >
                          {notifications.length}
                        </Badge>
                      )}
                    </Button>
                    {notificationsVisible && (
                      <div className="absolute top-full right-0 mt-2 z-50 w-80">
                        <NotificationContainer
                          onClose={onClickCloseNotification}
                        />
                      </div>
                    )}
                  </div>
                )}

                {user.authenticated ? (
                  <div className="relative">
                    <ProfilesCard
                      setProfileDetailsVisible={setProfileDetailsVisible}
                      triggerButton={
                        <button
                          type="button"
                          aria-label="Профиль"
                          className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          onClick={() =>
                            setProfileDetailsVisible((prev) => !prev)
                          }
                        >
                          <ProfileAvatar
                            customPlaceholder={
                              currentProfile.currentType === 'company' &&
                              !currentProfile.avatarFilesIds
                                ? AvatarPlaceholder
                                : undefined
                            }
                            avatarFiles={currentProfile.avatarFilesIds || ''}
                            size={40}
                          />
                        </button>
                      }
                    />
                  </div>
                ) : (
                  <div className="hidden md:flex items-center gap-2">
                    <Button
                      variant="ghost"
                      onClick={onClickLogin}
                      className="font-medium text-base hover:text-primary hover:underline px-3"
                    >
                      Войти
                    </Button>
                  </div>
                )}
              </div>

              <div className="md:hidden">
                <ProfilesCard
                  triggerButton={
                    <Button variant="ghost" size="icon">
                      <Menu className="h-6 w-6 text-gray-600" />
                    </Button>
                  }
                />
              </div>
            </div>
          </Container>
        </header>

        <ModalTenderCreate
          companyId={company?.id || null}
          visible={modalTenderCreateVisible}
          closeModal={closeModalTenderCreate}
          onSuccess={handleTenderCreateSuccess}
        />
      </>
    );
  }
);

export default Header;
